<template>
  <ConfirmDialog />
  <DomainInstructions
    v-if="displayDialog"
    :display-dialog="displayDialog"
    :data="sendDomainsData"
    :type="type"
    :locale="locale"
    @on-close-dialog="closeDialog"
    @hide-dialog="displayDialog =false"
    @refresh-data-table="dataTableKey+=1"
  />
  <div class="mt-2 my-domains">
    <div class="flex">
      <div class="flex flex-wrap mb-4">
        <h3>
          {{ t('shop.menu.send_domains') }}
        </h3>
      </div>
      <div
        v-if="findPermission('my_domains.create')"
        class="flex flex-wrap mb-4"
        style="margin-left: auto;"
      >
        <CreateDomain @refresh-data-table="dataTableKey+=1" />
      </div>
    </div>
    <div
      v-if="showMessage"
      class="flex-1"
    >
      <div v-if="domainValid">
        <Message severity="success">
          {{ t('shop.my_domains.validate_success') }}
        </Message>
      </div>
      <div v-else>
        <Message severity="error">
          {{ t('shop.my_domains.validate_failure') }}
        </Message>
      </div>
    </div>
    <div class="grid">
      <div class="col-12">
        <SpmTableApi
          :key="dataTableKey"
          ref="childComponentRef"
          type-api="client_api"
          route="/domains"
          name="ShopsSendDomains"
          index="id_domain"
          :table-columns="columns"
          :persistent-filters="persistentFilters"
          :id-shop="idShop ?? 0"
          @data-loaded="handleDataLoaded"
        >
          <template #summary>
            <div class="my-domains__summary-card">
              <i class="fa-regular fa-compass" />
              <div>
                <h4>{{ t('shop.my_domains.deliverability.summary.title') }}</h4>
                <p>{{ t('shop.my_domains.deliverability.summary.desc') }}</p>
              </div>
            </div>
          </template>
          <template #domain="slotProps">
            <div class="domain-card">
              <div class="domain-card__header">
                <div class="domain-card__title">
                  <h4>{{ slotProps.data.domain }}</h4>
                  <div class="domain-card__score">
                    <span class="domain-card__score-value">{{ getDeliverabilityScore(slotProps.data) }}%</span>
                    <span class="domain-card__score-label">{{ t('shop.my_domains.deliverability.score_label') }}</span>
                  </div>
                </div>
                <div
                  class="domain-card__weather"
                  :class="`domain-card__weather--${getWeatherState(slotProps.data)}`"
                >
                  <i :class="getWeatherIcon(slotProps.data)" />
                  <span>{{ getWeatherLabel(slotProps.data) }}</span>
                </div>
                <div class="domain-card__actions">
                  <ActionRenderer
                    :actions="actions"
                    :data="slotProps.data"
                  />
                </div>
              </div>

              <div
                v-if="getDeliverabilityScore(slotProps.data) !== 100"
                class="domain-card__alert"
              >
                <div class="domain-card__alert-content">
                  <i class="fa-solid fa-triangle-exclamation" />
                  <span>{{ t('shop.my_domains.deliverability.score_hint') }}</span>
                </div>
              </div>

              <div class="domain-card__timeline">
                <div
                  v-for="(step, index) in getSteps(slotProps.data)"
                  :key="step.key"
                  class="domain-card__step"
                  :class="`domain-card__step--${step.state}`"
                >
                  <div class="domain-card__step-icon">
                    <i :class="step.icon" />
                  </div>
                  <div class="domain-card__step-content">
                    <div class="domain-card__step-header">
                      <span>{{ step.label }}</span>
                      <i :class="step.weatherIcon" />
                    </div>
                    <p>{{ step.description }}</p>
                    <div
                      v-if="step.actions.length"
                      class="domain-card__step-actions"
                    >
                      <Button
                        v-for="action in step.actions"
                        :key="action.label"
                        class="p-button p-button-rounded p-button-sm"
                        :class="action.variant"
                        :label="action.label"
                        @click="action.onClick()"
                      />
                    </div>
                  </div>
                  <div
                    v-if="index < totalSteps - 1"
                    class="domain-card__connector"
                  />
                </div>
              </div>
            </div>
          </template>
        </SpmTableApi>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { useI18n } from 'vue-i18n';
import {
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
} from 'vue';

import Button from 'primevue/button';

import SpmTableApi from '@/components/table/SpmTableApi.vue';
import { SpmTableAction, SpmTableColumns, SpmTableFilter } from '@/types';
import { OperatorType, ShopsSendDomains } from '@/types/generated-types/graphql';
import { findPermission, UserState } from '@/composables/User';
import DomainInstructions from '@/views/shop/DomainInstructions.vue';
import CreateDomain from '@/components/shop/my-domains/CreateDomain.vue';
import { useConfirm } from 'primevue/useconfirm';
import { DeleteShopsSendDomains, ValidateShopDomainByToken, VerifyDomain } from '@/composables/shop/ShopsSendDomains';
import { showToastError, showToastSuccess } from '@/helpers';
import ConfirmDialog from 'primevue/confirmdialog';
import Message from 'primevue/message';
import ActionRenderer from '@/components/table/renderers/ActionRenderer.vue';

// @see class Menu in node_modules/primevue/menu/Menu.d.ts
interface MenuInterface {
  toggle(event: Event): void;
}

export default defineComponent({
  name: 'MyDomains',
  components: {
    DomainInstructions,
    SpmTableApi,
    CreateDomain,
    ConfirmDialog,
    Message,
    Button,
    ActionRenderer,
  },

  props: {
    token: {
      type: String,
      required: false,
      default: undefined,
    },
  },

  setup(props) {
    const { t } = useI18n();
    const dataTableKey = ref(0);
    const confirm = useConfirm();
    const idShop = UserState.activeShop?.id;
    const locale = UserState.user.lang;
    const domainValid = ref(false);
    const showMessage = ref(false);
    const childComponentRef = ref();
    const verifyingDns = ref<Set<number>>(new Set());
    const pendingDomainChecks = ref<Map<number, number>>(new Map());
    const domainRowMap = ref<Map<number, ShopsSendDomains>>(new Map());
    const pollTimer = ref<number | null>(null);
    const POLLING_INTERVAL_MS = 10000;
    const MAX_POLL_ATTEMPTS = 100;
    const TOTAL_STEPS = 3;

    type StepState = 'done' | 'progress' | 'todo' | 'warning';
    type WeatherState = 'storm' | 'rain' | 'clouds' | 'sun';

    interface StepActionConfig {
      label: string;
      variant: string;
      onClick: () => void;
    }

    interface StepDefinition {
      key: string;
      label: string;
      description: string;
      icon: string;
      state: StepState;
      actions: StepActionConfig[];
      weatherIcon: string;
    }

    const weatherIcons: Record<WeatherState, string> = {
      storm: 'fa-solid fa-cloud-bolt',
      rain: 'fa-solid fa-cloud-rain',
      clouds: 'fa-solid fa-cloud-sun',
      sun: 'fa-solid fa-sun',
    };

    const stepWeatherIcons: Record<StepState, string> = {
      done: 'fa-solid fa-sun',
      progress: 'fa-solid fa-cloud-sun',
      todo: 'fa-solid fa-cloud',
      warning: 'fa-solid fa-triangle-exclamation',
    };

    // Only value 1 is considered as verified (handles string/boolean/number)
    const isVerified = (value: any): boolean => {
      if (typeof value === 'string') {
        return value === '1' || value === 'true';
      }
      return value === true || value === 1;
    };

    // Values 2, 3, 4 represent temporary invalid state
    const isTemporaryInvalid = (value: any): boolean => {
      if (typeof value === 'string') {
        return value === '2' || value === '3' || value === '4';
      }
      return value === 2 || value === 3 || value === 4;
    };

    const getCompletedSteps = (domain: ShopsSendDomains): number => {
      let completed = 0;

      if (isVerified(domain.authentication_verified)) completed += 1;
      if (isVerified(domain.alignment_verified)) completed += 1;
      if (isVerified(domain.tracking_domain_verified)) completed += 1;

      return completed;
    };

    const getDeliverabilityScore = (domain: ShopsSendDomains): number => {
      const completed = getCompletedSteps(domain);
      return Math.round((completed / TOTAL_STEPS) * 100);
    };

    const getWeatherState = (domain: ShopsSendDomains): WeatherState => {
      const completed = getCompletedSteps(domain);

      if (completed >= 3) {
        return 'sun';
      }

      if (completed === 2) {
        return 'clouds';
      }

      if (completed === 1) {
        return 'rain';
      }

      return 'storm';
    };

    const getWeatherIcon = (domain: ShopsSendDomains): string => weatherIcons[getWeatherState(domain)];

    const getWeatherLabel = (domain: ShopsSendDomains): string => t(`shop.my_domains.deliverability.weather.${getWeatherState(domain)}`);

    const buildAuthenticationActions = (domain: ShopsSendDomains, state: StepState): StepActionConfig[] => {
      if (state === 'done') {
        return [{
          label: t('shop.my_domains.deliverability.actions.review'),
          variant: 'p-button-secondary',
          onClick: () => showDialog(domain, 'authenticationInstructionNewMethod'),
        }];
      }

      if (state === 'progress') {
        return [{
          label: t('shop.my_domains.deliverability.actions.resume'),
          variant: 'p-button-warning',
          onClick: () => showDialog(domain, 'authenticationInstructionNewMethod'),
        }];
      }

      return [{
        label: t('shop.my_domains.deliverability.actions.start'),
        variant: 'p-button-success',
        onClick: () => showDialog(domain, 'authenticationInstruction'),
      }];
    };

    const buildAlignmentActions = (domain: ShopsSendDomains, state: StepState): StepActionConfig[] => {
      if (state === 'done') {
        return [{
          label: t('shop.my_domains.modify'),
          variant: 'p-button-secondary',
          onClick: () => showDialog(domain, 'alignmentInstruction'),
        }];
      }

      return [{
        label: t('shop.my_domains.deliverability.actions.start'),
        variant: 'p-button-success',
        onClick: () => showDialog(domain, 'alignmentInstruction'),
      }];
    };

    const buildTrackingActions = (domain: ShopsSendDomains, state: StepState): StepActionConfig[] => {
      if (state === 'done') {
        return [{
          label: t('shop.my_domains.modify'),
          variant: 'p-button-secondary',
          onClick: () => showDialog(domain, 'trackingDomainInstruction'),
        }];
      }

      return [{
        label: t('shop.my_domains.deliverability.actions.start'),
        variant: 'p-button-success',
        onClick: () => showDialog(domain, 'trackingDomainInstruction'),
      }];
    };

    const getAuthenticationState = (domain: ShopsSendDomains): StepState => {
      if (isVerified(domain.authentication_verified)) {
        return 'done';
      }

      if (isTemporaryInvalid(domain.authentication_verified)) {
        return 'warning';
      }

      if (domain.dkim_verified && domain.public_key !== null) {
        return 'progress';
      }

      return 'todo';
    };

    const getAlignmentState = (domain: ShopsSendDomains): StepState => {
      if (isVerified(domain.alignment_verified)) {
        return 'done';
      }
      if (isTemporaryInvalid(domain.alignment_verified)) {
        return 'warning';
      }
      return 'todo';
    };

    const getTrackingState = (domain: ShopsSendDomains): StepState => {
      if (isVerified(domain.tracking_domain_verified)) {
        return 'done';
      }
      if (isTemporaryInvalid(domain.tracking_domain_verified)) {
        return 'warning';
      }
      return 'todo';
    };

    const getSteps = (domain: ShopsSendDomains): StepDefinition[] => {
      const authenticationState = getAuthenticationState(domain);
      const alignmentState = getAlignmentState(domain);
      const trackingState = getTrackingState(domain);

      return [
        {
          key: 'authentication',
          label: t('shop.my_domains.deliverability.steps.authentication.title'),
          description: t('shop.my_domains.deliverability.steps.authentication.desc'),
          icon: 'fa-solid fa-shield-halved',
          state: authenticationState,
          actions: buildAuthenticationActions(domain, authenticationState),
          weatherIcon: stepWeatherIcons[authenticationState],
        },
        {
          key: 'alignment',
          label: t('shop.my_domains.deliverability.steps.alignment.title'),
          description: t('shop.my_domains.deliverability.steps.alignment.desc'),
          icon: 'fa-solid fa-pen-ruler',
          state: alignmentState,
          actions: buildAlignmentActions(domain, alignmentState),
          weatherIcon: stepWeatherIcons[alignmentState],
        },
        {
          key: 'tracking',
          label: t('shop.my_domains.deliverability.steps.tracking.title'),
          description: t('shop.my_domains.deliverability.steps.tracking.desc'),
          icon: 'fa-solid fa-compass-drafting',
          state: trackingState,
          actions: buildTrackingActions(domain, trackingState),
          weatherIcon: stepWeatherIcons[trackingState],
        },
      ];
    };

    const stopPolling = () => {
      if (pollTimer.value !== null) {
        window.clearTimeout(pollTimer.value);
        pollTimer.value = null;
      }
    };

    const normalizeDomainId = (domain: ShopsSendDomains): number | null => {
      if (typeof domain.id_domain === 'number') {
        return domain.id_domain;
      }

      if (typeof domain.id_domain === 'string') {
        const parsedId = Number(domain.id_domain);

        if (!Number.isNaN(parsedId)) {
          return parsedId;
        }
      }

      return null;
    };

    const updateDomainRowMap = (rows: ShopsSendDomains[]) => {
      const seenIds = new Set<number>();

      rows.forEach((row) => {
        const domainId = normalizeDomainId(row);

        if (domainId === null) {
          return;
        }

        domainRowMap.value.set(domainId, row);
        seenIds.add(domainId);
      });

      [...domainRowMap.value.keys()].forEach((domainId) => {
        if (!seenIds.has(domainId)) {
          domainRowMap.value.delete(domainId);
          pendingDomainChecks.value.delete(domainId);
          verifyingDns.value.delete(domainId);
        }
      });
    };

    const registerPendingDomains = (domains: ShopsSendDomains[]) => {
      const currentPendingIds = new Set<number>();

      domains.forEach((domain) => {
        const domainId = normalizeDomainId(domain);

        if (domainId === null) {
          return;
        }

        if (!domain.domain_verified) {
          currentPendingIds.add(domainId);

          if (!pendingDomainChecks.value.has(domainId)) {
            pendingDomainChecks.value.set(domainId, 0);
          }
        } else {
          pendingDomainChecks.value.delete(domainId);
        }
      });

      [...pendingDomainChecks.value.keys()].forEach((domainId) => {
        if (!currentPendingIds.has(domainId)) {
          pendingDomainChecks.value.delete(domainId);
        }
      });

      if (pendingDomainChecks.value.size === 0) {
        stopPolling();
      }
    };

    const incrementPollAttempts = () => {
      const attemptsMap = pendingDomainChecks.value;

      [...attemptsMap.keys()].forEach((domainId) => {
        const attempts = attemptsMap.get(domainId) ?? 0;
        const nextAttempt = attempts + 1;

        if (nextAttempt >= MAX_POLL_ATTEMPTS) {
          attemptsMap.delete(domainId);
        } else {
          attemptsMap.set(domainId, nextAttempt);
        }
      });

      if (attemptsMap.size === 0) {
        stopPolling();
        return false;
      }

      return true;
    };

    const applyDomainVerificationUpdates = (
      domainId: number,
      updates: Pick<ShopsSendDomains,
      'domain_verified' | 'authentication_verified' | 'alignment_verified' | 'tracking_domain_verified'>,
    ) => {
      const targetDomain = domainRowMap.value.get(domainId);

      if (!targetDomain) {
        return false;
      }

      const previousState = {
        domain_verified: targetDomain.domain_verified,
        authentication_verified: targetDomain.authentication_verified,
        alignment_verified: targetDomain.alignment_verified,
        tracking_domain_verified: targetDomain.tracking_domain_verified,
      };

      Object.assign(targetDomain, updates);

      if (targetDomain.domain_verified) {
        pendingDomainChecks.value.delete(domainId);
      }

      return previousState.domain_verified !== targetDomain.domain_verified
        || previousState.authentication_verified !== targetDomain.authentication_verified
        || previousState.alignment_verified !== targetDomain.alignment_verified
        || previousState.tracking_domain_verified !== targetDomain.tracking_domain_verified;
    };

    const verifyDomains = async (domains: ShopsSendDomains[]) => {
      const verificationTasks = domains.map(async (domain) => {
        const domainId = normalizeDomainId(domain);

        if (domainId === null) {
          return false;
        }

        verifyingDns.value.add(domainId);

        try {
          const { data, err } = await VerifyDomain(domainId);

          if (err || !data) {
            return false;
          }

          return applyDomainVerificationUpdates(domainId, {
            domain_verified: data.domain_verified,
            authentication_verified: data.authentication_verified,
            alignment_verified: data.alignment_verified,
            tracking_domain_verified: data.tracking_domain_verified,
          });
        } catch (error) {
          console.error('Failed to auto-verify domain DNS', error);
        } finally {
          verifyingDns.value.delete(domainId);
        }

        return false;
      });

      await Promise.all(verificationTasks);
    };

    const schedulePoll = () => {
      if (pollTimer.value !== null || pendingDomainChecks.value.size === 0) {
        return;
      }

      pollTimer.value = window.setTimeout(async () => {
        pollTimer.value = null;

        const shouldContinue = incrementPollAttempts();

        if (!shouldContinue) {
          return;
        }

        const pendingIds = [...pendingDomainChecks.value.keys()]
          .filter((domainId) => !verifyingDns.value.has(domainId));

        const domainsToVerify = pendingIds
          .map((domainId) => domainRowMap.value.get(domainId))
          .filter((domain): domain is ShopsSendDomains => Boolean(domain));

        if (domainsToVerify.length > 0) {
          await verifyDomains(domainsToVerify);
        }

        schedulePoll();
      }, POLLING_INTERVAL_MS);
    };
    const shopId = UserState?.activeShop?.id ?? 0; // todo: to do something for invalid shop
    const actions: SpmTableAction[] = [
      {
        label: t('shop.my_domains.delete.actionText'),
        icon: 'far fa-trash',
        callback: (data: ShopsSendDomains, event: Event | undefined) => {
          if (!event) return;
          confirm.require({
            message: t('shop.my_domains.delete.confirmText'),
            header: t('shop.my_domains.delete.confirmHeader'),
            icon: 'fa-regular fa-square-info',
            acceptClass: 'p-button-danger',
            accept: async () => {
              const result = await DeleteShopsSendDomains([{ id_domain: data.id_domain }]);

              if (result.status) {
                await showToastSuccess('shop.my_domains.delete.success');
                dataTableKey.value += 1;
              } else {
                await showToastError('shop.my_domains.delete.error');
              }
            },
          });
        },

        show: () => findPermission('my_domains.delete'),
      },
    ];
    const columns: SpmTableColumns[] = [
      {
        field: 'domain',
        header: t('shop.my_domains.headers.domain.title'),
        sortable: true,
        filterable: true,
        editable: false,
        style: '',
        type: 'text',
      },
      {
        field: 'domain_verified',
        header: t('shop.my_domains.headers.verification.title'),
        sortable: false,
        filterable: false,
        editable: false,
        style: '',
        type: 'text',
        hidden: true,
      },
      {
        field: 'authentication_verified',
        header: t('shop.my_domains.headers.authentication.title'),
        sortable: false,
        filterable: false,
        editable: false,
        style: '',
        type: 'text',
        hidden: true,
      },
      {
        field: 'alignment_verified',
        header: t('shop.my_domains.headers.alignment.title'),
        sortable: false,
        filterable: false,
        editable: false,
        style: '',
        type: 'text',
        hidden: true,
      },
      {
        field: 'tracking_domain_verified',
        header: t('shop.my_domains.headers.tracking.title'),
        sortable: false,
        filterable: false,
        editable: false,
        style: '',
        type: 'text',
        hidden: true,
      },
      {
        field: 'dkim_verified',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: '',
        type: '',
        hidden: true,
      },
      {
        field: 'public_key',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: '',
        type: '',
        hidden: true,
      },
      {
        field: 'id_shop',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: '',
        type: '',
        hidden: true,
      },
      {
        field: 'tracking_domain',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: '',
        type: '',
        hidden: true,
      },
      {
        field: 'tracking_domain_default_medias',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: '',
        type: '',
        hidden: true,
      },
      {
        field: 'id_domain',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: '',
        type: '',
        hidden: true,
      },
      {
        field: 'dns_verification_key',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: '',
        type: '',
        hidden: true,
      },
      {
        field: 'alignment_domain',
        header: '',
        sortable: false,
        filterable: false,
        editable: false,
        style: '',
        type: '',
        hidden: true,
      },
    ];
    const persistentFilters: SpmTableFilter[] = [{ field: 'id_shop', value: shopId, operator: OperatorType.Equals }];

    const displayDialog = ref<boolean>(false);
    const sendDomainsData = ref<ShopsSendDomains>();
    const type = ref('');
    const closeDialog = (refresh: boolean) => {
      displayDialog.value = false;
    };

    const showDialog = (data: ShopsSendDomains, dialogType: string) => {
      displayDialog.value = true;
      sendDomainsData.value = data;
      type.value = dialogType;
    };

    const handleDataLoaded = async (rows: ShopsSendDomains[]) => {
      if (!Array.isArray(rows) || rows.length === 0) {
        return;
      }

      updateDomainRowMap(rows);

      registerPendingDomains(rows);

      const pendingVerification = rows.filter((row) => {
        const domainId = normalizeDomainId(row);

        return domainId !== null
          && !row.domain_verified
          && !verifyingDns.value.has(domainId);
      });

      if (pendingVerification.length > 0) {
        await verifyDomains(pendingVerification);
      }

      schedulePoll();
    };

    onMounted(async () => {
      if (props.token && props.token !== '') {
        const result = await ValidateShopDomainByToken(props.token, idShop ?? 0);

        if (result.status && result.id) {
          domainValid.value = true;
          dataTableKey.value += 1;
        }

        showMessage.value = true;
        setTimeout(() => {
          showMessage.value = false;
        }, 5000);
      }
    });

    onBeforeUnmount(() => {
      stopPolling();
      pendingDomainChecks.value.clear();
      verifyingDns.value.clear();
      domainRowMap.value.clear();
    });

    return {
      t,
      columns,
      persistentFilters,
      findPermission,
      actions,
      showDialog,
      sendDomainsData,
      displayDialog,
      type,
      dataTableKey,
      closeDialog,
      domainValid,
      showMessage,
      locale,
      childComponentRef,
      idShop,
      handleDataLoaded,
      getDeliverabilityScore,
      getWeatherIcon,
      getWeatherLabel,
      getWeatherState,
      getSteps,
      totalSteps: TOTAL_STEPS,
    };
  },
});
</script>

<style scoped lang="scss">
.my-domains {
  padding-bottom: 2rem;

  :deep(.spm-data-table .p-datatable-wrapper .p-datatable-tbody tr:hover, .spm-data-table .p-datatable-wrapper .p-datatable-tbody tr:not(.p-highlight):hover) {
    background: transparent!important;
    background-color: transparent!important;
  }

  :deep(.p-datatable) {
    background: transparent;
    border: 0;
  }

  :deep(.p-datatable-header) {
    background: transparent;
    border: 0;
    padding: 0 0 1.5rem 0;
  }

  :deep(.p-datatable-wrapper) {
    background: transparent;
  }

  :deep(.p-datatable-thead) {
    display: none;
  }

  :deep(.p-datatable-tbody > tr > td) {
    border: 0;
    padding: 0 0 2rem 0;
  }

  :deep(.p-datatable-tbody > tr) {
    background: transparent;
  }
}

.my-domains__summary-card {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  background: linear-gradient(135deg, rgba(147, 197, 253, 0.2), rgba(148, 200, 64, 0.15));
  border-radius: 20px;
  padding: 1.5rem 2rem;
  color: #1b2a4b;

  i {
    font-size: 1.75rem;
    color: #1b2a4b;
  }

  h4 {
    margin: 0 0 0.35rem 0;
    font-weight: 600;
  }

  p {
    margin: 0;
    color: #2e3e5c;
    font-size: 0.95rem;
    line-height: 1.5;
  }
}

.domain-card {
  position: relative;
  background: #ffffff;
  border-radius: 26px;
  padding: 1.75rem 2rem;
  border: 1px solid rgba(17, 63, 103, 0.08);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.domain-card__header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 1.5rem;
}

.domain-card__title {
  flex: 1 1 240px;

  h4 {
    margin: 0 0 0.5rem 0;
    font-size: 1.35rem;
    font-weight: 600;
    color: #13233d;
  }
}

.domain-card__score {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.domain-card__score-value {
  font-size: 2rem;
  font-weight: 700;
  color: #0e7a3f;
  line-height: 1;
}

.domain-card__score-label {
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #4a5b78;
}

.domain-card__weather {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  border-radius: 999px;
  padding: 0.75rem 1.25rem;
  font-weight: 600;
  font-size: 0.95rem;

  i {
    font-size: 1.25rem;
  }
}

.domain-card__weather--storm {
  background: rgba(211, 47, 47, 0.12);
  color: #b71c1c;
}

.domain-card__weather--rain {
  background: rgba(239, 172, 6, 0.15);
  color: #bb6f00;
}

.domain-card__weather--clouds {
  background: rgba(76, 114, 178, 0.16);
  color: #2f4a7f;
}

.domain-card__weather--sun {
  background: rgba(148, 200, 64, 0.18);
  color: #4b7b06;
}

.domain-card__actions {
  margin-left: auto;

  :deep(.action-renderer-trigger) {
    color: #1b2a4b;
  }
}

.domain-card__alert {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border: 1px dashed rgba(239, 172, 6, 0.6);
  padding: 1rem 1.25rem;
  border-radius: 16px;
  color: #925900;
}

.domain-card__alert-content {
  display: flex;
  gap: 0.75rem;
  align-items: center;

  i {
    font-size: 1.2rem;
  }
}

.domain-card__alert-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.domain-card__timeline {
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem;
}

.domain-card__step {
  position: relative;
  flex: 1 1 220px;
  min-width: 220px;
  padding: 1.2rem 1.1rem 1.4rem 1.1rem;
  border-radius: 20px;
  border: 1px solid rgba(27, 42, 75, 0.08);
  background: #f7f9fc;
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.domain-card__step--done {
  border-color: rgba(148, 200, 64, 0.35);
  background: linear-gradient(135deg, rgba(148, 200, 64, 0.18), rgba(255, 255, 255, 0.9));
}

.domain-card__step--progress {
  border-color: rgba(239, 172, 6, 0.45);
  background: linear-gradient(135deg, rgba(239, 172, 6, 0.18), rgba(255, 255, 255, 0.92));
}

.domain-card__step--todo {
  border-style: dashed;
  border-color: rgba(27, 42, 75, 0.15);
  background: rgba(243, 246, 250, 0.9);
  color: #607296;
}

.domain-card__step--warning {
  border-color: rgba(239, 172, 6, 0.45);
  background: linear-gradient(135deg, rgba(239, 172, 6, 0.18), rgba(255, 255, 255, 0.92));
}

.domain-card__step-icon {
  width: 48px;
  height: 48px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(27, 42, 75, 0.08);
  color: #1b2a4b;
  font-size: 1.3rem;
}

.domain-card__step--done .domain-card__step-icon {
  background: rgba(148, 200, 64, 0.25);
  color: #4f7c0b;
}

.domain-card__step--progress .domain-card__step-icon {
  background: rgba(239, 172, 6, 0.24);
  color: #b57700;
}

.domain-card__step--warning .domain-card__step-icon {
  background: rgba(239, 172, 6, 0.24);
  color: #b57700;
}

.domain-card__step-content {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  flex: 1;
}

.domain-card__step-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  color: inherit;

  i {
    font-size: 1rem;
  }
}

.domain-card__step-actions {
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
}

.domain-card__connector {
  position: absolute;
  right: -1.5rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1.5rem;
  height: 2px;
  background: rgba(27, 42, 75, 0.15);
}

.domain-card__hint {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  font-size: 0.9rem;
  color: #4a5b78;
  background: rgba(19, 35, 61, 0.05);
  border-radius: 14px;
  padding: 0.85rem 1.1rem;

  i {
    color: #f5b400;
  }
}

@media screen and (max-width: 768px) {
  .domain-card {
    padding: 1.5rem;
  }

  .domain-card__score {
    flex-direction: row;
    align-items: baseline;
    gap: 0.5rem;
  }

  .domain-card__score-value {
    font-size: 1.6rem;
  }

  .domain-card__weather {
    width: 100%;
    justify-content: center;
  }

  .domain-card__timeline {
    flex-direction: column;
  }

  .domain-card__step {
    min-width: auto;
  }

  .domain-card__connector {
    display: none;
  }
}
</style>

<style lang="scss">
.instruction-btn-wrapper {
  .p-button-icon-left {
    margin-right: 0.3rem !important;
  }
}
</style>
